import { useEffect } from 'react';

import { useBreakpointsContext } from '@air/provider-media-query';
import { preSelectedIdsSelector, selectableItemsSelector } from '@air/redux-selected-items';

import { isSelectableGalleryItem } from '~/store/selectedItems/types';
import { useAirStore } from '~/utils/ReduxUtils';
import { modifiersToCommitParams, useSelectionCommit } from './useSelectionCommit';

/**
 * A single, stable `mouseup` listener that confirms whatever gallery item is currently
 * pre-selected (a card's mousedown sets `preSelectedIds` in Redux). Mounted once on a
 * stable gallery owner — NOT per card — so a card that remounts mid-press during the
 * gallery's initial settle can't tear it down. The pre-selected id survives the remount
 * in Redux, so the release still commits the selection.
 *
 * This is additive: the per-card mouseup listeners stay and handle the normal case. The
 * commit clears `preSelectedIds`, so whichever fires first wins and the other no-ops —
 * this owner only matters when the card's own listener was lost to a remount.
 */
export function useConfirmPreSelectedItemOnMouseUp() {
  const store = useAirStore();
  const { commitSelection } = useSelectionCommit();
  const { isAboveMediumScreen } = useBreakpointsContext();

  useEffect(() => {
    const onMouseUp = (event: MouseEvent) => {
      if (!isAboveMediumScreen) {
        return;
      }

      const state = store.getState();
      const preSelectedId = preSelectedIdsSelector(state)[0];
      if (!preSelectedId) {
        return;
      }

      const item = selectableItemsSelector(state).find(({ id }) => id === preSelectedId);
      if (!item || !isSelectableGalleryItem(item)) {
        return;
      }

      commitSelection(
        item,
        modifiersToCommitParams({ shiftKey: event.shiftKey, metaKey: event.metaKey, ctrlKey: event.ctrlKey }),
      );
    };

    window.addEventListener('mouseup', onMouseUp);
    return () => window.removeEventListener('mouseup', onMouseUp);
  }, [store, commitSelection, isAboveMediumScreen]);
}
